import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatAUD } from "@/lib/api";
import { Plus, ArrowUpRight } from "lucide-react";

const STATUS_LABEL = { draft: "Draft", sent: "Sent", accepted: "Accepted", declined: "Declined" };

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");
  const nav = useNavigate();

  const load = () => api.get("/projects").then((r) => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    const { data } = await api.post("/projects", {
      title: "New Project", status: "draft", room_type: "Living Room",
      wall_width_mm: 3000, wall_height_mm: 2400, wall_depth_mm: 400,
      schematic: { items: [] }, line_items: [],
      labour_rate: 0.15, margin_rate: 0.20, tax_rate: 0.10, discount: 0,
    });
    nav(`/projects/${data.id}`);
  };

  const list = filter === "all" ? projects : projects.filter((p) => p.status === filter);

  return (
    <div className="px-6 lg:px-14 py-10 lg:py-14 max-w-7xl" data-testid="projects-page">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="overline">Studio · Workflow</div>
          <h1 className="font-serif font-light mt-3" style={{ fontSize: "44px", lineHeight: 1.05 }}>Projects</h1>
          <p className="text-muted-foreground mt-3">From initial brief to final proposal — manage every project in your studio.</p>
        </div>
        <button data-testid="btn-new-project-list" onClick={create} className="btn-primary">
          <Plus className="w-4 h-4" strokeWidth={1.5}/> New project
        </button>
      </div>

      <div className="flex gap-1 mb-6 border-b border-border overflow-x-auto -mx-6 px-6 lg:mx-0 lg:px-0 whitespace-nowrap">
        {["all", "draft", "sent", "accepted", "declined"].map((s) => (
          <button
            key={s}
            data-testid={`filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-5 py-3 text-sm tracking-wide shrink-0 ${filter === s ? "border-b-2 border-foreground font-medium" : "text-muted-foreground"}`}>
            {s === "all" ? "All" : STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <div className="luxe-card p-16 text-center">
          <div className="font-serif text-2xl mb-2">No projects {filter !== "all" ? `in ${STATUS_LABEL[filter]}` : "yet"}</div>
          <p className="text-sm text-muted-foreground mb-6">Begin designing your first media wall proposal.</p>
          <button data-testid="btn-new-project-empty" onClick={create} className="btn-accent mx-auto">
            <Plus className="w-4 h-4" strokeWidth={1.5}/> New project
          </button>
        </div>
      ) : (
        <div className="luxe-card overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead>
              <tr className="border-b border-border" style={{ background: "#F4F2EC" }}>
                <th className="text-left px-6 py-4 overline text-[10px]">Project</th>
                <th className="text-left px-6 py-4 overline text-[10px] hidden md:table-cell">Client</th>
                <th className="text-left px-6 py-4 overline text-[10px] hidden lg:table-cell">Room</th>
                <th className="text-right px-6 py-4 overline text-[10px]">Total</th>
                <th className="text-right px-6 py-4 overline text-[10px]">Status</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className="border-b border-border last:border-0 hover:bg-[color:hsl(var(--muted))]/40 cursor-pointer" onClick={() => nav(`/projects/${p.id}`)} data-testid={`project-row-${p.id}`}>
                  <td className="px-6 py-5">
                    <div className="font-serif text-lg">{p.title}</div>
                    <div className="text-xs text-muted-foreground">Updated {new Date(p.updated_at).toLocaleDateString("en-AU")}</div>
                  </td>
                  <td className="px-6 py-5 hidden md:table-cell text-muted-foreground">{p.client_name || "—"}</td>
                  <td className="px-6 py-5 hidden lg:table-cell text-muted-foreground">{p.room_type}</td>
                  <td className="px-6 py-5 text-right font-medium">{formatAUD(p.totals?.total)}</td>
                  <td className="px-6 py-5 text-right">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${statusClasses(p.status)}`}>{STATUS_LABEL[p.status] || p.status}</span>
                  </td>
                  <td className="px-6 py-5 text-right text-muted-foreground"><ArrowUpRight className="w-4 h-4 inline" strokeWidth={1.5}/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function statusClasses(s) {
  switch (s) {
    case "accepted": return "bg-[color:hsl(var(--accent))] text-white";
    case "sent": return "bg-foreground text-background";
    case "declined": return "bg-[color:hsl(var(--destructive))] text-white";
    default: return "bg-muted text-foreground";
  }
}
